/*
Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.

Licensed under the Apache License, Version 2.0 (the "License").
You may not use this file except in compliance with the License.
You may obtain a copy of the License at

    http://www.apache.org/licenses/LICENSE-2.0

Unless required by applicable law or agreed to in writing, software
distributed under the License is distributed on an "AS IS" BASIS,
WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
See the License for the specific language governing permissions and
limitations under the License.
*/
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  Menu,
  MenuItem,
  Divider,
  Breadcrumbs,
  Link,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@mui/material';
import JSZip from 'jszip';
import { useDispatch, useSelector } from 'react-redux';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import RefreshIcon from '@mui/icons-material/Refresh';
import CreateNewFolderOutlinedIcon from '@mui/icons-material/CreateNewFolderOutlined';
import ArrowBackOutlinedIcon from '@mui/icons-material/ArrowBackOutlined';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  ReferenceStoreDto,
  ReferenceStoreDtoStatusEnum,
  ReferenceStoreApi,
} from '@ink-ai/insight-service-sdk';
import { RootState } from '@ink-ai/portal/reducers';
import { LoadingButton } from '@mui/lab';
import { ReferenceStoreTable } from '../../components/reference-store/ReferenceStoreListTable';
import {
  deleteReferenceStores,
  handleDownload,
  listReferenceStores,
  referenceStoreActions,
} from '@ink-ai/portal/reducers/reference-store';
import { PaperCard } from '../../components/Common';
import { DirectoryMoveDialog } from '../../components/reference-store/DirectoryMoveDialog';
import { hasAnyRole, adminRolesForEnv, superAdminRolesForEnv, isDefaultProtectedName } from '@ink-ai/portal/common/authHelper';
import { getApi, makeRequest } from '@ink-ai/portal/common/requestHelper';
import { app } from '@ink-ai/portal/reducers/app';

export const ReferenceStore: React.FC = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch<any>();
  const referenceStore = useSelector(
    (state: RootState) => state.referenceStore,
  );
  const navigate = useNavigate();
  const location = useLocation();
  const auth = useSelector((state: RootState) => state.auth);
  const isAdmin = hasAnyRole(auth.group, adminRolesForEnv());
  const isSuperAdmin = hasAnyRole(auth.group, superAdminRolesForEnv());
  const [addingFolder, setAddingFolder] = useState(false);
  const [draftFolderName, setDraftFolderName] = useState('');
  const isReferenceManagement = location.pathname === '/reference-management';
  const actionsButtonRef = useRef<HTMLButtonElement>(null);
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedReferenceStores, setSelectedReferenceStores] = useState<
    string[]
  >([]);
  const [selectedFolders, setSelectedFolders] = useState<string[]>([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [menuLoading, setMenuLoading] = useState(false);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [pendingDeleteIds, setPendingDeleteIds] = useState<string[]>([]);
  // 删除上下文：区分单行删除与批量删除
  const [deleteContext, setDeleteContext] = useState<'row' | 'bulk'>('bulk');
  // Folder deletion state
  const [confirmDeleteFolderOpen, setConfirmDeleteFolderOpen] = useState(false);
  const [pendingDeleteFolderId, setPendingDeleteFolderId] = useState<string | null>(null);
  const [pendingDeleteFolderName, setPendingDeleteFolderName] = useState<string>('');
  // Explorer state: folders + references
  type ExplorerItem = {
    uuid: string;
    name: string;
    type: 'folder' | 'reference';
    desc?: string;
    status?: ReferenceStoreDtoStatusEnum;
    createdAt?: string | number;
    updatedAt?: string | number;
    itemCount?: number;
    parentId?: string | null;
  };
  const [explorerItems, setExplorerItems] = useState<ExplorerItem[]>([]);
  const [explorerTotal, setExplorerTotal] = useState<number>(0);
  const [currentFolderId, setCurrentFolderId] = useState<string | null>(null);
  const [folderStack, setFolderStack] = useState<{ id: string | null; name: string }[]>([{ id: null, name: t('referenceStore.navigation.referenceStore') }]);
  const currentFolderName = folderStack[folderStack.length - 1]?.name;
  // "default"-protected rule (front-end mirror of the server guard). Only
  // applies when actually inside a folder (root's stack entry is just a title).
  const currentFolderProtected = !!currentFolderId && isDefaultProtectedName(currentFolderName);
  const canCreateHere = isSuperAdmin || !currentFolderProtected;
  const canMutateRow = React.useCallback(
    (row: any) => isSuperAdmin || (!isDefaultProtectedName(row?.name) && !currentFolderProtected),
    [isSuperAdmin, currentFolderProtected],
  );
  const pendingDeleteNames = React.useMemo(() => {
    if (!pendingDeleteIds || pendingDeleteIds.length === 0) return [] as string[];
    return explorerItems.filter((it) => it.type === 'reference' && pendingDeleteIds.includes(it.uuid)).map((it) => it.name);
  }, [pendingDeleteIds, explorerItems]);
  const pendingDeleteFolderNames = React.useMemo(() => {
    if (!selectedFolders || selectedFolders.length === 0) return [] as string[];
    return explorerItems
      .filter((it) => it.type === 'folder' && selectedFolders.includes(it.uuid))
      .map((it) => it.name);
  }, [selectedFolders, explorerItems]);

  // Move dialog state
  const [moveDialogOpen, setMoveDialogOpen] = useState(false);
  const [moveTargetIds, setMoveTargetIds] = useState<string[]>([]);

  // Load explorer items (root or specific folder)
  const loadExplorer = async () => {
    console.log('ReferenceStoreList: loadExplorer called');
    try {
      dispatch(referenceStoreActions.setLoading(true));
      const params: Record<string, any> = {
        page: page + 1,
        pageSize: rowsPerPage,
      };
      if (searchTerm && searchTerm.trim().length > 0) params.name = searchTerm.trim();
      if (currentFolderId) params.parentId = currentFolderId;
      const data: any = await makeRequest({
        method: 'GET',
        url: '/api/v1/referenceStore/referenceExplorer',
        params,
      });
      // 新版 API 使用 collections 作为返回键，并在条目中提供 itemType 字段；
      // 兼容旧版 items 结构，优先读取 collections。
      const rawItems =
        data.collections ||
        data.data?.collections ||
        data.items ||
        data.data?.items ||
        [];
      const toMillis = (v: any) => {
        if (v == null) return undefined as any;
        if (typeof v === 'number') return v;
        return new Date(v).getTime();
      };
      const statusMap: Record<string, ReferenceStoreDtoStatusEnum> = {
        READY: ReferenceStoreDtoStatusEnum.Ready,
        IMPORTING: ReferenceStoreDtoStatusEnum.Importing,
        ERROR: ReferenceStoreDtoStatusEnum.Error,
      } as any;
      const items: ExplorerItem[] = rawItems.map((it: any) => {
        const itemType = String(it.itemType || it.type || '').toLowerCase();
        const isFolder = itemType === 'folder';
        const isRef = itemType === 'file' || itemType === 'reference';
        const createdAt = toMillis(it.createdAt);
        const updatedAt = toMillis(it.updatedAt);

        if (isFolder) {
          return {
            uuid: it.uuid,
            name: it.name,
            type: 'folder',
            desc: undefined,
            status: undefined,
            createdAt,
            updatedAt,
            // 新版返回中 folder 使用 count 表示直辖文件数量；也可能包含 collections 列表
            itemCount:
              typeof it.count === 'number'
                ? it.count
                : Array.isArray(it.collections)
                ? it.collections.length
                : undefined,
            parentId: it.parentId ?? null,
          } as ExplorerItem;
        }

        // 默认按 reference（文件）处理
        return {
          uuid: it.uuid,
          name: it.name,
          type: 'reference',
          desc: it.desc,
          // 传递错误信息以便在状态列展示 Tooltip
          errorMessage: it.errorMessage,
          status:
            statusMap[String((it.status || 'READY')).toUpperCase()] ?? it.status,
          createdAt,
          updatedAt,
          itemCount: it.itemCount ?? it.itemsCount,
          parentId: it.parentId ?? currentFolderId ?? null,
        } as ExplorerItem;
      });
      const total = data.totalCount ?? data.data?.totalCount ?? items.length;
      setExplorerItems(items);
      setExplorerTotal(total);
    } catch (e) {
      console.error('Failed to load reference store explorer:', e);
    } finally {
      dispatch(referenceStoreActions.setLoading(false));
    }
  };

  useEffect(() => {
    loadExplorer();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, rowsPerPage, searchTerm, currentFolderId]);

  const openMoveDialogFromSelection = () => {
    setMoveTargetIds(selectedReferenceStores);
    setMoveDialogOpen(true);
    handleMenuClose();
  };

  const openMoveDialogFromRow = (row?: ReferenceStoreDto) => {
    const ids = row ? [row.uuid] : selectedReferenceStores;
    setMoveTargetIds(ids);
    setMoveDialogOpen(true);
  };

  const handleMoveCancel = () => {
    setMoveDialogOpen(false);
    setMoveTargetIds([]);
  };

  const handleMoveConfirm = async (folderId: string | null, itemIds: string[]) => {
    await moveItemsToFolder(folderId, itemIds);
    setMoveDialogOpen(false);
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
    setPage(0);
    // 变更搜索时，避免草稿文件夹误显示
    setAddingFolder(false);
    setDraftFolderName('');
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleDelete = () => {
    if ((selectedReferenceStores.length === 0 && selectedFolders.length === 0) || menuLoading) return;
    setDeleteContext('bulk');
    setPendingDeleteIds(selectedReferenceStores);
    setConfirmDeleteOpen(true);
    handleMenuClose();
  };

  const handleExportCsv = async () => {
    setMenuLoading(true);
    try {
      // 辅助：获取某文件夹下的所有参考文件 ID
      const fetchFolderFileIds = async (folderId: string): Promise<string[]> => {
        const ids: string[] = [];
        let pageIdx = 1;
        const pageSize = 1000;
        while (true) {
          const data: any = await makeRequest({
            method: 'GET',
            url: '/api/v1/referenceStore/referenceExplorer',
            params: { parentId: folderId, page: pageIdx, pageSize },
          });
          const raw = data.collections || data.data?.collections || data.items || data.data?.items || [];
          const files = raw.filter((it: any) => {
            const t = String(it.itemType || it.type || '').toLowerCase();
            const s = String(it.status || '').toLowerCase();
            return (t === 'file' || t === 'reference') && s !== 'importing';
          });
          ids.push(...files.map((f: any) => f.uuid));
          const total = data.total ?? data.data?.total ?? raw.length;
          if (pageIdx * pageSize >= total || files.length === 0) break;
          pageIdx += 1;
        }
        return Array.from(new Set(ids));
      };

      if (selectedFolders.length === 0) {
        if (selectedReferenceStores.length === 1) {
          const id = selectedReferenceStores[0];
          const item = explorerItems.find((it) => it.uuid === id);
          if (item?.status !== ReferenceStoreDtoStatusEnum.Importing) {
            await handleDownload(id, item?.name);
          }
        } else {
          // 多选：打包为 ZIP 后一次性下载
          const zip = new JSZip();
          const referenceStoreApi = await getApi(ReferenceStoreApi);
          const usedNames = new Set<string>();

          const sanitize = (s: string) => s.replace(/[\\/:*?"<>|]/g, '_').replace(/[\r\n\t]/g, '').trim();
          const makeUnique = (name: string) => {
            if (!usedNames.has(name)) {
              usedNames.add(name);
              return name;
            }
            let i = 1;
            let candidate = `${name} (${i})`;
            while (usedNames.has(candidate)) {
              i += 1;
              candidate = `${name} (${i})`;
            }
            usedNames.add(candidate);
            return candidate;
          };

          const extFromMime = (mime: string) => {
            const m = (mime || '').toLowerCase();
            const map: Record<string, string> = {
              'text/csv': '.csv',
              'application/json': '.json',
              'text/plain': '.txt',
              'application/pdf': '.pdf',
              'application/vnd.ms-excel': '.xls',
              'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': '.xlsx',
            };
            return map[m] || '';
          };

          const readySelectedIds = selectedReferenceStores.filter((id) => {
            const sel = explorerItems.find((it) => it.uuid === id);
            return sel?.type === 'reference' && sel?.status !== ReferenceStoreDtoStatusEnum.Importing;
          });
          for (const id of readySelectedIds) {
            try {
              const metaResp = await referenceStoreApi.getReferenceStoreById(id);
              const meta = metaResp.data as any;
              const name = meta?.name || id;
              
              const { data } = await referenceStoreApi.getReferenceStoreDownloadLink(id);
              const url = data.downloadLink;
              const resp = await fetch(url);
              const blob = await resp.blob();

              // 统一命名：优先名称；补全扩展名（来自 header 或 URL）；最后回退到 UUID
              const cd = resp.headers.get('content-disposition') || '';
              const match = cd.match(/filename\*=UTF-8''([^;]+)|filename="?([^";]+)"?/i);
              const headerNameRaw = match?.[1] || match?.[2] || '';
              // RFC 5987: filename*=UTF-8''<encoded>; 需要 decodeURIComponent
              const headerName = match?.[1] ? decodeURIComponent(headerNameRaw) : headerNameRaw;
              let urlName = '';
              try {
                const u = new URL(url);
                urlName = decodeURIComponent(
                  u.pathname.substring(u.pathname.lastIndexOf('/') + 1)
                );
              } catch {}
              const extSource = headerName || urlName;
              let ext = '';
              if (extSource && extSource.includes('.')) {
                ext = extSource.substring(extSource.lastIndexOf('.'));
              }
              if (!ext) {
                const contentType = resp.headers.get('content-type') || blob.type || '';
                ext = extFromMime(contentType);
              }
              // const item = referenceStore.referenceStores.find((g) => g.uuid === id);
              let base = sanitize(name || headerName || urlName || id);
              if (ext && !base.toLowerCase().endsWith(ext.toLowerCase())) {
                base += ext;
              }
              base = makeUnique(base);

              zip.file(base, blob);
            } catch (e) {
              console.error('Failed to add reference store to zip:', e);
            }
          }

          const zipBlob = await zip.generateAsync({ type: 'blob' });
          const a = document.createElement('a');
          const objectUrl = URL.createObjectURL(zipBlob);
          a.href = objectUrl;
          a.download = 'reference-stores.zip';
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
          URL.revokeObjectURL(objectUrl);
        }
      } else {
        // 选择了文件夹（可能同时包含文件）：沿用原有逐文件打包 ZIP 方式
        const fromFolders = (await Promise.all(selectedFolders.map((id) => fetchFolderFileIds(id)))).flat();
        const readySelectedIds = selectedReferenceStores.filter((id) => {
          const sel = explorerItems.find((it) => it.uuid === id);
          return sel?.type === 'reference' && sel?.status !== ReferenceStoreDtoStatusEnum.Importing;
        });
        const allFileIds = Array.from(new Set([...readySelectedIds, ...fromFolders]));
        if (allFileIds.length === 0) return;

        const zip = new JSZip();
        const referenceStoreApi = await getApi(ReferenceStoreApi);
        const usedNames = new Set<string>();

        const sanitize = (s: string) => s.replace(/[\\/:*?"<>|]/g, '_').replace(/[\r\n\t]/g, '').trim();
        const makeUnique = (name: string) => {
          if (!usedNames.has(name)) {
            usedNames.add(name);
            return name;
          }
          let i = 1;
          let candidate = `${name} (${i})`;
          while (usedNames.has(candidate)) {
            i += 1;
            candidate = `${name} (${i})`;
          }
          usedNames.add(candidate);
          return candidate;
        };
        const extFromMime = (mime: string) => {
          const m = (mime || '').toLowerCase();
          const map: Record<string, string> = {
            'text/csv': '.csv',
            'application/json': '.json',
            'text/plain': '.txt',
            'application/pdf': '.pdf',
            'application/vnd.ms-excel': '.xls',
            'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': '.xlsx',
          };
          return map[m] || '';
        };

        for (const id of allFileIds) {
          try {
            // 获取元信息以优先使用条目名称
            const metaResp = await referenceStoreApi.getReferenceStoreById(id);
            const meta = metaResp.data as any;
            const name = meta?.name || id;

            const { data } = await referenceStoreApi.getReferenceStoreDownloadLink(id);
            const url = data.downloadLink;
            const resp = await fetch(url);
            const blob = await resp.blob();

            const cd = resp.headers.get('content-disposition') || '';
            const match = cd.match(/filename\*=UTF-8''([^;]+)|filename="?([^";]+)"?/i);
            const headerNameRaw = match?.[1] || match?.[2] || '';
            const headerName = match?.[1] ? decodeURIComponent(headerNameRaw) : headerNameRaw;
            let urlName = '';
            try {
              const u = new URL(url);
              urlName = decodeURIComponent(
                u.pathname.substring(u.pathname.lastIndexOf('/') + 1)
              );
            } catch {}
            const extSource = headerName || urlName;
            let ext = '';
            if (extSource && extSource.includes('.')) {
              ext = extSource.substring(extSource.lastIndexOf('.'));
            }
            if (!ext) {
              const contentType = resp.headers.get('content-type') || blob.type || '';
              ext = extFromMime(contentType);
            }
            let base = sanitize(name || headerName || urlName || id);
            if (ext && !base.toLowerCase().endsWith(ext.toLowerCase())) {
              base += ext;
            }
            base = makeUnique(base);

            zip.file(base, blob);
          } catch (e) {
            console.error('Failed to add reference store to zip (folder selection):', e);
          }
        }

        const zipBlob = await zip.generateAsync({ type: 'blob' });
        const a = document.createElement('a');
        const objectUrl = URL.createObjectURL(zipBlob);
        a.href = objectUrl;
        a.download = 'reference-stores.zip';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(objectUrl);
      }
    } catch (error) {
      console.error('Error exporting reference stores:', error);
    } finally {
      setMenuLoading(false);
      handleMenuClose();
    }
  };

  const handleUpdate = () => {
    if (selectedReferenceStores.length === 1) {
      const selId = selectedReferenceStores[0];
      // 优先使用 explorerItems（与当前列表同步）
      const selectedItem = explorerItems.find((it) => it.uuid === selId);
      const selectedReferenceStore = selectedItem || referenceStore.referenceStores.find((g) => g.uuid === selId);
      if (selectedReferenceStore) {
        navigate('/reference-management/import', {
          state: {
            referenceStoreUuid: selectedReferenceStore.uuid,
            referenceStoreName: selectedReferenceStore.name,
            referenceStoreDescription: selectedReferenceStore.desc,
          },
        });
      }
    }
    handleMenuClose();
  };

  // Row-level actions
  const handleRowDownload = async (row: ReferenceStoreDto) => {
    try {
      await handleDownload(row.uuid, row.name);
    } catch (error) {
      console.error('Error downloading reference store:', error);
    }
  };

  const handleRowDelete = (row: ReferenceStoreDto) => {
    if (menuLoading) return;
    // 行删除仅删除该行，避免受已勾选文件夹影响
    // setSelectedFolders([]);
    setDeleteContext('row');
    setPendingDeleteIds([row.uuid]);
    setConfirmDeleteOpen(true);
  };

  // Request delete folder -> open confirm dialog
  const handleFolderDelete = (folderUuid: string) => {
    if (menuLoading) return;
    const name = explorerItems.find((it) => it.type === 'folder' && it.uuid === folderUuid)?.name || '';
    setPendingDeleteFolderId(folderUuid);
    setPendingDeleteFolderName(name);
    setConfirmDeleteFolderOpen(true);
  };

  // Confirm delete folder
  const handleConfirmDeleteFolder = async () => {
    if (!pendingDeleteFolderId) return;
    setMenuLoading(true);
    try {
      await makeRequest({
        method: 'DELETE',
        url: `/api/v1/referenceStore/folders/${pendingDeleteFolderId}`,
        params: { cascade: true, force: true },
      });
      await loadExplorer();
    } catch (error) {
      console.error('Error deleting folder:', error);
    } finally {
      setMenuLoading(false);
      setConfirmDeleteFolderOpen(false);
      setPendingDeleteFolderId(null);
      setPendingDeleteFolderName('');
    }
  };

  const handleRowUpdate = (row: ReferenceStoreDto) => {
    navigate('/reference-management/import', {
      state: {
        referenceStoreUuid: row.uuid,
        referenceStoreName: row.name,
        referenceStoreDescription: row.desc,
      },
    });
  };

  const handleNavigate = (referenceStore: ReferenceStoreDto) => {
    dispatch(referenceStoreActions.setSelectedReferenceStore(referenceStore));
    const targetPath = isReferenceManagement
      ? `/reference-management/${referenceStore.uuid}`
      : `/reference-store/${referenceStore.uuid}`;
    navigate(targetPath, {
      state: {
        from: isReferenceManagement ? '/reference-management' : '/reference-store',
      },
    });
  };

  const handleCsvExportDisabled = () => {
    // 允许多选批量下载；仅在未选择任何项时禁用
    return selectedReferenceStores.length === 0;
  };

  const handleUpdateDisabled = () => {
    if (selectedReferenceStores.length !== 1) return true;
    const selId = selectedReferenceStores[0];
    const selectedItem = explorerItems.find((it) => it.uuid === selId);
    const selectedReferenceStore = selectedItem || referenceStore.referenceStores.find((g) => g.uuid === selId);
    return selectedReferenceStore?.status === ReferenceStoreDtoStatusEnum.Importing;
  };
 
  const hasReadyFileSelected = React.useMemo(() => {
    if (!selectedReferenceStores || selectedReferenceStores.length === 0) return false;
    const readyIds = new Set(
      explorerItems
        .filter((it: any) => String(it.type || '').toLowerCase() !== 'folder' && it.status !== ReferenceStoreDtoStatusEnum.Importing)
        .map((it: any) => it.uuid)
    );
    return selectedReferenceStores.some((id) => readyIds.has(id));
  }, [explorerItems, selectedReferenceStores]);

  const handleEnterFolder = (folderId: string, folderName?: string) => {
    // 进入新文件夹时，清除新增文件夹草稿行，避免混淆
    setAddingFolder(false);
    setDraftFolderName('');
    // 进入文件夹时清空勾选状态
    setSelectedReferenceStores([]);
    setSelectedFolders([]);
    setCurrentFolderId(folderId);
    setFolderStack((prev) => [...prev, { id: folderId, name: folderName || t('referenceStore.title') }]);
    setPage(0);
  };

  const handleBack = () => {
    if (folderStack.length <= 1) return;
    const next = [...folderStack];
    next.pop();
    // 返回上一层或根目录时清空勾选状态
    setSelectedReferenceStores([]);
    setSelectedFolders([]);
    setFolderStack(next);
    setCurrentFolderId(next[next.length - 1]?.id || null);
    setPage(0);
    // 返回上一层时，同样清除新增文件夹草稿
    setAddingFolder(false);
    setDraftFolderName('');
  };

  const handleRefresh = () => {
    loadExplorer();
    // 刷新时清除草稿，确保列表与后端返回一致
    setAddingFolder(false);
    setDraftFolderName('');
  };

  const handleConfirmAddFolder = async () => {
    if (!draftFolderName || draftFolderName.trim().length === 0) return;
    try {
      setMenuLoading(true);
      await makeRequest({
        method: 'POST',
        url: '/api/v1/referenceStore/folders',
        // 强制在根目录创建文件夹
        data: { name: draftFolderName.trim(), parentId: null },
      });
      setAddingFolder(false);
      setDraftFolderName('');
      await loadExplorer();
    } catch (e) {
      console.error('Add folder failed:', e);
      const msg = (e as any)?.response?.data?.message ?? (e as any)?.data?.message ?? (e as any)?.message ?? String(e);
      dispatch(app.actions.setGlobalMessage({ message: msg, status: 'error' }));
      setAddingFolder(true);
      setDraftFolderName(draftFolderName.trim());
    } finally {
      setMenuLoading(false);
    }
  };

  const moveItemsToFolder = async (folderId: string | null, itemIds: string[]) => {
    try {
      setMenuLoading(true);
      await makeRequest({
        method: 'POST',
        url: '/api/v1/referenceStore/move',
        data: { itemUuids: itemIds, folderId: folderId ?? null },
      });
      await loadExplorer();
      setSelectedReferenceStores([]);
      setSelectedFolders([]);
      return true;
    } catch (e) {
      console.error('Move items failed:', e);
      return false;
    } finally {
      setMenuLoading(false);
    }
  };

  const handleConfirmDelete = async () => {
    const isRow = deleteContext === 'row';
    const hasFiles = !!pendingDeleteIds && pendingDeleteIds.length > 0;
    const hasFolders = !isRow && selectedFolders.length > 0;
    if (!hasFiles && !hasFolders) return;
    setMenuLoading(true);
    try {
      dispatch(referenceStoreActions.setLoading(true));
      if (hasFiles) {
        await dispatch(deleteReferenceStores(pendingDeleteIds));
        setSelectedReferenceStores((prev) => prev.filter((id) => !pendingDeleteIds.includes(id)));
      }
      // 仅在批量删除上下文处理文件夹删除
      if (hasFolders) {
        try {
          await makeRequest({
            method: 'POST',
            url: '/api/v1/referenceStore/folders/batch-delete',
            data: { items: selectedFolders, cascade: true, force: true },
          });
          setSelectedFolders([]);
        } catch (error) {
          console.error('Error batch deleting reference folders:', error);
        }
      }
      await loadExplorer();
    } catch (error) {
      console.error('Error deleting reference store:', error);
    } finally {
      setMenuLoading(false);
      setConfirmDeleteOpen(false);
      setPendingDeleteIds([]);
      setDeleteContext('bulk');
    }
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 1, mb: 1 }}>
      <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 2 }}>
        <Link
          color="inherit"
          onClick={() => navigate('/')}
          sx={{ textDecoration: 'none', cursor: 'pointer' }}
        >
          {t('referenceStore.navigation.home')}
        </Link>
        {/* <Typography color="textPrimary">
          {
            isReferenceManagement && isAdmin
              ?
              t('referenceStore.navigation.referenceManagement')
              :
              t('referenceStore.navigation.referenceStore')
          }
        </Typography> */}
        {folderStack.length > 1 ? (
            <Link
              color="inherit"
              onClick={handleBack}
              sx={{ textDecoration: 'none', cursor: 'pointer' }}
            >
              {isReferenceManagement && isAdmin
                ? t('referenceStore.navigation.referenceManagement')
                : t('referenceStore.navigation.referenceStore')}
            </Link>
        ) : (
          <Typography color="textPrimary">
            {isReferenceManagement && isAdmin
              ? t('referenceStore.navigation.referenceManagement')
              : t('referenceStore.navigation.referenceStore')}
          </Typography>
        )}
        {
          folderStack.length > 1 &&
          <Typography color="textPrimary">
            {folderStack[folderStack.length - 1]?.name}
          </Typography>
        }
      </Breadcrumbs>
      <PaperCard>
        <Typography variant="h5" sx={{ textAlign: 'left', mb: 2 }}>
          {currentFolderId ? (currentFolderName || t('referenceStore.title')) : (
            isReferenceManagement && isAdmin ? t('referenceStore.navigation.referenceManagement') : t('referenceStore.title')
          )}
        </Typography>
        <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between' }}>
          <TextField
            label={t('referenceStore.search.label')}
            variant="outlined"
            size="small"
            value={searchTerm}
            InputLabelProps={{
              shrink: true,
            }}
            onChange={handleSearchChange}
          />
          <Box>
            {folderStack.length > 1 && (
              <Button sx={{ mr: 1 }} aria-label="back-folder" variant="outlined" onClick={handleBack}>
                <ArrowBackOutlinedIcon />
              </Button>
            )}
            {
              isReferenceManagement && isAdmin && !currentFolderId &&
              <Button
                sx={{ mr: 1 }}
                aria-label="add-folder"
                variant="outlined"
                onClick={() => {
                  setAddingFolder(true);
                  setDraftFolderName('');
                }}
              >
                <CreateNewFolderOutlinedIcon />
              </Button>
            }
            <Button
              sx={{ mr: 1 }}
              aria-label="refresh"
              variant="outlined"
              onClick={handleRefresh}
            >
              <RefreshIcon />
            </Button>
            {
              isReferenceManagement && isAdmin && (
                <>
                  <LoadingButton
                    ref={actionsButtonRef}
                    onClick={handleMenuOpen}
                    endIcon={<ArrowDropDownIcon />}
                    variant="outlined"
                    disabled={(selectedReferenceStores.length === 0 && selectedFolders.length === 0) || addingFolder}
                    loading={menuLoading}
                  >
                    {t('referenceStore.actions.button')}
                  </LoadingButton>
                  {canCreateHere && (
                    <Button
                      variant="contained"
                      color="primary"
                      sx={{ ml: 1 }}
                      onClick={() => navigate('/reference-management/import')}
                    >
                      {t('referenceStore.actions.import')}
                    </Button>
                  )}
                  <Menu
                    anchorEl={anchorEl}
                    open={Boolean(anchorEl)}
                    onClose={handleMenuClose}
                    slotProps={{
                      paper: {
                        style: {
                          minWidth: actionsButtonRef.current?.offsetWidth,
                        },
                      },
                    }}
                  >
                  {/* <MenuItem
                      onClick={handleExportCsv}
                      disabled={handleCsvExportDisabled() || menuLoading}
                    >
                      {t('referenceStore.actions.exportCsv')}
                    </MenuItem> */}
                    <MenuItem
                      onClick={handleExportCsv}
                      disabled={(selectedReferenceStores.length === 0 && selectedFolders.length === 0) || menuLoading || (selectedFolders.length === 0 && selectedReferenceStores.length > 0 && !hasReadyFileSelected)}
                    >
                      {t('referenceStore.actions.download')}
                    </MenuItem>
                    {/* <MenuItem
                      onClick={handleDelete}
                      disabled={(selectedReferenceStores.length === 0 && selectedFolders.length === 0) || menuLoading}
                    >
                      {t('referenceStore.actions.delete')}
                    </MenuItem> */}
                    <MenuItem
                      onClick={openMoveDialogFromSelection}
                      disabled={(selectedReferenceStores.length === 0) || menuLoading}
                    >
                      {t('referenceStore.actions.move')}
                    </MenuItem>
                    <MenuItem
                      onClick={handleUpdate}
                      disabled={handleUpdateDisabled() || menuLoading}
                    >
                      {t('referenceStore.actions.update')}
                    </MenuItem>
                    <Divider />
                    <MenuItem
                      onClick={handleDelete}
                      disabled={(selectedReferenceStores.length === 0 && selectedFolders.length === 0) || menuLoading}
                    >
                      <Typography color="error">
                        {t('referenceStore.actions.delete')}
                      </Typography>
                    </MenuItem>
                  </Menu>
                </>
              )
            }
          </Box>
        </Box>
        <ReferenceStoreTable
          items={explorerItems}
          selectedReferenceStores={selectedReferenceStores}
          onSelectedReferenceStoresChange={setSelectedReferenceStores}
          selectedFolders={selectedFolders}
          onSelectedFoldersChange={setSelectedFolders}
          searchTerm={searchTerm}
          rowsPerPage={rowsPerPage}
          onNavigate={handleNavigate}
          onEnterFolder={(id, name) => handleEnterFolder(id, name)}
          onDeleteFolderClick={handleFolderDelete}
          onMoveClick={(row) => openMoveDialogFromRow(row)}
          onDownloadClick={handleRowDownload}
          onDeleteClick={handleRowDelete}
          onUpdateClick={handleRowUpdate}
          page={page}
          onPageChange={setPage}
          onRowsPerPageChange={setRowsPerPage}
          enableSelection={isReferenceManagement && isAdmin}
          showActions={isReferenceManagement && isAdmin}
          canMutateRow={canMutateRow}
          addingFolder={addingFolder}
          draftFolderName={draftFolderName}
          onDraftFolderNameChange={(name) => setDraftFolderName(name)}
          onConfirmAddFolder={handleConfirmAddFolder}
          onCancelAddFolder={() => {
            setAddingFolder(false);
            setDraftFolderName('');
          }}
          onReloadExplorer={() => { console.log('ReferenceStoreList: onReloadExplorer'); loadExplorer(); }}
          totalCount={explorerTotal}
        />
        <DirectoryMoveDialog
          open={moveDialogOpen}
          itemIds={moveTargetIds}
          onCancel={handleMoveCancel}
          onConfirm={handleMoveConfirm}
          fetchFolders={async (page, pageSize) => {
            try {
              // const p = Math.max(0, page) + 1;
              // const data: any = await makeRequest({
              //   method: 'GET',
              //   url: '/api/v1/referenceStore/referenceExplorer',
              //   params: { page: p, pageSize },
              // });
              // const raw =
              //   data.collections ||
              //   data.data?.collections ||
              //   data.items ||
              //   data.data?.items ||
              //   [];
              // const folders = raw
              //   .filter((it: any) => {
              //     const t = String(it.itemType || it.type || '').toLowerCase();
              //     return t === 'folder';
              //   })
              //   .map((f: any) => ({ id: f.uuid, name: f.name }));
              // console.log('[Reference] move-dialog folders loaded', { page: p, pageSize, count: folders.length });
              // return { total: data.total ?? data.data?.total ?? folders.length, items: folders };
              const p = Math.max(0, page) + 1;
              const data: any = await makeRequest({
                method: 'GET',
                url: '/api/v1/referenceStore/folders',
                params: { parentId: 'undefined', page: p, pageSize },
              });
              const raw = data.folders || data.data?.folders || [];
              const folders = raw.map((f: any) => ({ id: f.uuid, name: f.name }));
              console.log('[Reference] move-dialog folders loaded', { page: p, pageSize, count: folders.length });
              return { total: data.totalCount ?? data.total ?? data.data?.total ?? folders.length, items: folders };
            } catch (e) {
              console.error('Fetch reference folders failed:', e);
              return { total: 0, items: [] };
            }
          }}
          fetchFiles={async (folderId, page, pageSize) => {
            try {
              const p = Math.max(0, page) + 1;
              const data: any = await makeRequest({
                method: 'GET',
                url: '/api/v1/referenceStore/referenceExplorer',
                params: { parentId: folderId, page: p, pageSize },
              });
              const raw =
                data.collections ||
                data.data?.collections ||
                data.items ||
                data.data?.items ||
                [];
              const files = raw
                .filter((it: any) => {
                  const t = String(it.itemType || it.type || '').toLowerCase();
                  return t === 'file' || t === 'reference';
                })
                .map((f: any) => ({ id: f.uuid, name: f.name }));
              console.log('[Reference] move-dialog files loaded', { folderId, page: p, pageSize, count: files.length });
              return { total: data.total ?? data.data?.total ?? files.length, items: files };
            } catch (e) {
              console.error('Fetch reference files in folder failed:', e);
              return { total: 0, items: [] };
            }
          }}
        />
        <Dialog
          open={confirmDeleteOpen}
          onClose={() => setConfirmDeleteOpen(false)}
        >
          <DialogTitle>{t('referenceStore.actions.delete')}</DialogTitle>
          <DialogContent>
            <DialogContentText>
              {deleteContext === 'bulk' && selectedFolders.length > 0 && pendingDeleteIds.length > 0
                ? t('referenceStore.confirmDelete.messageMixed', { folderCount: selectedFolders.length, fileCount: pendingDeleteIds.length })
                : deleteContext === 'bulk' && selectedFolders.length > 0
                ? t('referenceStore.confirmDelete.messageFolders', { count: selectedFolders.length })
                : t('referenceStore.confirmDelete.messageFiles', { count: pendingDeleteIds.length })}
            </DialogContentText>
            {/* 注释掉名称展示，避免 pendingDeleteNames 问题影响使用 */}
            {/* {pendingDeleteFolderNames.length > 0 && (
              <Typography variant="body2" sx={{ mt: 1 }}>
                {t('referenceStore.confirmDelete.labels.folders')}{pendingDeleteFolderNames.join('、')}
              </Typography>
            )}
            {pendingDeleteNames.length > 0 && (
              <Typography variant="body2" sx={{ mt: 1 }}>
                {t('referenceStore.confirmDelete.labels.files')}{pendingDeleteNames.join('、')}
              </Typography>
            )} */}
            {deleteContext === 'bulk' && selectedFolders.length > 0 && (
              <Typography variant="body2" sx={{ mt: 1 }}>
                {t('referenceStore.confirmDeleteFolder.messageWithoutName')}
              </Typography>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setConfirmDeleteOpen(false)}>
              {t('common.cancel')}
            </Button>
            <LoadingButton
              color="error"
              variant="contained"
              onClick={handleConfirmDelete}
              loading={menuLoading}
            >
              {t('referenceStore.actions.delete')}
            </LoadingButton>
          </DialogActions>
        </Dialog>
        {/* Folder deletion confirm */}
        <Dialog
          open={confirmDeleteFolderOpen}
          onClose={() => {
            setConfirmDeleteFolderOpen(false);
            setPendingDeleteFolderId(null);
            setPendingDeleteFolderName('');
          }}
        >
          <DialogTitle>{t('referenceStore.actions.delete')}</DialogTitle>
          <DialogContent>
            <DialogContentText>
              {t('referenceStore.confirmDeleteFolder.message', { name: pendingDeleteFolderName })}
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => {
              setConfirmDeleteFolderOpen(false);
              setPendingDeleteFolderId(null);
              setPendingDeleteFolderName('');
            }}>
              {t('common.cancel')}
            </Button>
            <LoadingButton
              color="error"
              variant="contained"
              onClick={handleConfirmDeleteFolder}
              loading={menuLoading}
            >
              {t('referenceStore.actions.delete')}
            </LoadingButton>
          </DialogActions>
        </Dialog>
      </PaperCard>
    </Container>
  );
};
